import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { setToken, formatApiError } from "@/lib/api";
import { Shield, ArrowLeft, KeyRound, Mail, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submitLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.dev_code) { setDevCode(data.dev_code); setCode(data.dev_code); }
      setStep(2);
      toast.success("Verification code sent");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify", { email, code });
      setToken(data.token);
      setUser(data.user);
      if (data.user.must_change_password) nav("/set-password");
      else nav(data.user.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-[var(--bg-base)] text-white">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-white/10 bg-[var(--bg-surface)] relative overflow-hidden">
        <button onClick={() => nav("/")} data-testid="back-home-btn" className="flex items-center gap-2 text-[var(--txt-secondary)] hover:text-white transition-colors w-fit" style={{ fontSize: "16px" }}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div>
          <div className="overline mb-4" style={{ fontSize: "16px" }}>Secure access</div>
          <h1 className="font-display font-extrabold text-5xl tracking-tight leading-none mb-6">Step into the<br />control room.</h1>
          <p className="text-[var(--txt-secondary)] max-w-md leading-relaxed">Registered participants only. Two-step verification keeps your simulation data isolated and secure.</p>
        </div>
        <div className="font-mono text-[var(--txt-muted)]" style={{ fontSize: "16px" }}>TARDIGRADE · RESILIENCE SIMULATION</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <button type="button" onClick={() => nav("/")} data-testid="login-home-btn" className="flex items-center gap-2 mb-10 lg:hidden hover:opacity-80 transition-opacity">
            <div className="w-7 h-7 bg-white flex items-center justify-center"><Shield className="w-4 h-4 text-black" /></div>
            <span className="font-display font-extrabold text-lg">TARDIGRADE</span>
          </button>

          {step === 1 ? (
            <form onSubmit={submitLogin} className="space-y-5">
              <div>
                <h2 className="font-display font-bold text-2xl mb-1">Log in</h2>
                <p className="text-[var(--txt-muted)]" style={{ fontSize: "16px" }}>Enter your company email and password.</p>
              </div>
              <Field icon={Mail} label="Company email" type="email" value={email} onChange={setEmail} testid="login-email" placeholder="you@company.com" />
              <Field icon={KeyRound} label="Password" type="password" value={password} onChange={setPassword} testid="login-password" placeholder="••••••••" />
              {error && <p data-testid="login-error" className="text-[var(--urgent)]" style={{ fontSize: "16px" }}>{error}</p>}
              <button data-testid="login-submit-btn" disabled={loading} className="w-full bg-white text-black py-3 font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Continue
              </button>
            </form>
          ) : (
            <form onSubmit={submitVerify} className="space-y-5">
              <div>
                <h2 className="font-display font-bold text-2xl mb-1">Verify it's you</h2>
                <p className="text-sm text-[var(--txt-muted)]">Enter the 6-digit code sent to {email}.</p>
              </div>
              {devCode && (
                <div data-testid="dev-code-banner" className="elevated p-3 text-sm font-mono border-l-2 border-[var(--warning)]">
                  Dev mode code: <span className="text-[var(--warning)] font-bold">{devCode}</span>
                </div>
              )}
              <Field icon={KeyRound} label="Verification code" type="text" value={code} onChange={setCode} testid="verify-code" placeholder="000000" mono />
              {error && <p data-testid="verify-error" className="text-[var(--urgent)]" style={{ fontSize: "16px" }}>{error}</p>}
              <button data-testid="verify-submit-btn" disabled={loading} className="w-full bg-white text-black py-3 font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Enter simulation
              </button>
              <button type="button" onClick={() => { setStep(1); setError(""); }} className="text-sm text-[var(--txt-muted)] hover:text-white transition-colors">← Use a different account</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type, value, onChange, testid, placeholder, mono }) {
  const [show, setShow] = useState(false);
  const isPwd = type === "password";
  const inputType = isPwd ? (show ? "text" : "password") : type;
  return (
    <label className="block">
      <span className="overline block mb-2" style={{ fontSize: "16px" }}>{label}</span>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--txt-muted)] pointer-events-none" />
        <input data-testid={testid} type={inputType} value={value} required placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-[var(--bg-elevated)] pl-9 ${isPwd ? "pr-10" : "pr-3"} py-3 outline-none text-white placeholder:text-[var(--txt-muted)] ${mono ? "font-mono tracking-[0.3em]" : ""}`} />
        {isPwd && (
          <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--txt-muted)] hover:text-white transition-colors">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </label>
  );
}
